<template>
  <v-main>
    <v-container fluid>
      <!-- Header Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Feedback Questions</h3>
              <v-btn
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                @click="openCreateDialog"
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search questions..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="clearSearch"
                    @keydown.enter="getData"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.type"
                    :items="questionTypes"
                    item-text="text"
                    item-value="value"
                    clearable
                    @click:clear="clearType"
                    label="Question Type"
                    @input="getData"
                    prepend-inner-icon="mdi-tag"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="clearStatus"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Question</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">Status</th>
                      <th class="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="questions.length">
                    <tr
                      v-for="(question, i) in questions"
                      :key="`question_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': i === questions.length - 1,
                      }"
                    >
                      <td style="min-width: 85px">{{ i + 1 }}</td>
                      <td style="max-width: 400px">
                        {{ question.question_text }}
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="getTypeColor(question.type)"
                          text-color="white"
                        >
                          {{ getTypeLabel(question.type) }}
                        </v-chip>
                      </td>
                      <td>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              :color="question.is_active ? 'teal' : 'red'"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">
                                  {{
                                    question.is_active
                                      ? "mdi-checkbox-marked-circle"
                                      : "mdi-close-circle"
                                  }}
                                </v-icon>
                              </v-avatar>
                              {{ question.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </template>
                          <span>
                            Question is
                            {{ question.is_active ? "Active" : "Inactive" }}
                          </span>
                        </v-tooltip>
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-2"
                              color="primary"
                              @click="openEditDialog(question)"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit this Question</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="error"
                              @click="deleteQuestion(question.id)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete this Question</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="5" class="text-center">
                        <v-alert type="info" text>
                          No feedback questions found
                        </v-alert>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Pagination -->
      <v-row v-if="pagination.total_pages > 1">
        <v-col cols="12" class="d-flex justify-center">
          <v-pagination
            v-model="page"
            :length="pagination.total_pages"
            @input="getData"
            circle
          ></v-pagination>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: green; font-size: 90px">
                mdi-check-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <v-row>
                  <v-col>
                    <h1 style="color: green">Success!</h1>
                    <br />
                    <h3>{{ successMessage }}</h3>
                    <br />
                    <v-btn color="success" @click="successDialog = false">
                      OK
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: red; font-size: 90px">
                mdi-alert-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <v-row>
                  <v-col>
                    <h1 style="color: red">Error!</h1>
                    <br />
                    <h3>{{ errorMessage }}</h3>
                    <br />
                    <v-btn color="error" @click="errorDialog = false">
                      OK
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: orange; font-size: 90px">
                mdi-alert
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <v-row>
                  <v-col>
                    <h1 style="color: orange">Confirm Delete</h1>
                    <br />
                    <h3>
                      Are you sure you want to delete this feedback question?
                      This action cannot be undone.
                    </h3>
                    <br />
                    <v-btn
                      color="grey"
                      text
                      @click="deleteDialog = false"
                      class="mr-2"
                    >
                      Cancel
                    </v-btn>
                    <v-btn color="error" @click="confirmDelete">
                      Delete
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">
            {{ isEditMode ? "Edit" : "Create" }} Feedback Question
          </span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="formData.question_text"
                  label="Question Text *"
                  :rules="[rules.required]"
                  outlined
                  rows="3"
                  counter="1000"
                  hint="Enter the feedback question"
                  persistent-hint
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="formData.type"
                  :items="questionTypes"
                  item-text="text"
                  item-value="value"
                  label="Question Type *"
                  :rules="[rules.required]"
                  outlined
                  prepend-icon="mdi-tag"
                  hint="Select the type of feedback this question relates to"
                  persistent-hint
                >
                  <template v-slot:item="{ item }">
                    <v-chip small :color="getTypeColor(item.value)" dark>
                      {{ item.text }}
                    </v-chip>
                    <span class="ml-2 text-caption grey--text">
                      {{ item.description }}
                    </span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="formData.is_active"
                  label="Active Status"
                  color="success"
                  hint="Only active questions will be shown to users"
                  persistent-hint
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="isSaving"
            :disabled="!valid"
            @click="saveQuestion"
          >
            {{ isEditMode ? "Update" : "Create" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "FeedbackQuestionsIndex",
  data() {
    return {
      isLoading: false,
      isSaving: false,
      questions: [],
      dialog: false,
      successDialog: false,
      errorDialog: false,
      deleteDialog: false,
      successMessage: "",
      errorMessage: "",
      deleteQuestionId: null,
      isEditMode: false,
      valid: false,
      page: 1,
      filter: {
        search_keyword: null,
        type: null,
        is_active: null,
      },
      formData: {
        id: null,
        question_text: "",
        type: "product",
        is_active: true,
      },
      pagination: {
        total_pages: 1,
        total: 0,
        per_page: 10,
      },
      questionTypes: [],
      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      rules: {
        required: (value) => !!value || "This field is required",
      },
    };
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          rowsPerPage: this.pagination.per_page,
          page: this.page,
        };

        if (this.filter.search_keyword) {
          params.search_keyword = this.filter.search_keyword;
        }
        if (this.filter.type) {
          params.type = this.filter.type;
        }
        if (this.filter.is_active !== null && this.filter.is_active !== undefined) {
          params.is_active = this.filter.is_active;
        }

        const response = await axios.get("/feedback-questions", {
          params,
        });

        if (response && response.data) {
          if (response.data.data) {
            if (response.data.data.data) {
              // Paginated response
              this.questions = response.data.data.data;
              this.pagination.total_pages = response.data.data.last_page || 1;
              this.pagination.total = response.data.data.total || 0;
              this.pagination.per_page = response.data.data.per_page || 10;
            } else {
              // Non-paginated response
              this.questions = Array.isArray(response.data.data) ? response.data.data : [];
            }
          } else {
            this.questions = [];
          }
        }
      } catch (error) {
        console.error("Error fetching feedback questions:", error);
        this.questions = [];
        this.errorMessage = "Failed to load feedback questions. Please try again.";
        this.errorDialog = true;
      } finally {
        this.isLoading = false;
      }
    },

    openCreateDialog() {
      this.isEditMode = false;
      this.formData = {
        id: null,
        question_text: "",
        type: "product",
        is_active: true,
      };
      this.dialog = true;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    openEditDialog(question) {
      this.isEditMode = true;
      this.formData = {
        id: question.id,
        question_text: question.question_text,
        type: question.type,
        is_active: question.is_active,
      };
      this.dialog = true;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    closeDialog() {
      this.dialog = false;
      this.formData = {
        id: null,
        question_text: "",
        type: "product",
        is_active: true,
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    async saveQuestion() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      try {
        const payload = {
          question_text: this.formData.question_text,
          type: this.formData.type,
          is_active: this.formData.is_active,
        };

        let response;
        if (this.isEditMode) {
          // Update existing question
          response = await axios.put(
            `/feedback-questions/${this.formData.id}`,
            payload
          );
        } else {
          // Create new question
          response = await axios.post("/feedback-questions", payload);
        }

        if (response.data.success) {
          this.successMessage = this.isEditMode
            ? "Question updated successfully"
            : "Question created successfully";
          this.successDialog = true;
          this.closeDialog();
          this.getData();
        }
      } catch (error) {
        console.error("Error saving question:", error);
        this.errorMessage = error.response?.data?.message || "Failed to save question";
        this.errorDialog = true;
      } finally {
        this.isSaving = false;
      }
    },

    deleteQuestion(id) {
      this.deleteQuestionId = id;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleteDialog = false;
      try {
        const response = await axios.delete(
          `/feedback-questions/${this.deleteQuestionId}`
        );

        if (response.data.success) {
          this.successMessage = "Question deleted successfully";
          this.successDialog = true;
          this.getData();
        }
      } catch (error) {
        console.error("Error deleting question:", error);
        this.errorMessage =
          error.response?.data?.message || "Failed to delete question";
        this.errorDialog = true;
      } finally {
        this.deleteQuestionId = null;
      }
    },

    getTypeColor(type) {
      const colors = {
        product: "blue",
        showroom: "purple",
        sales: "orange",
        billing: "green",
        delivery: "indigo",
        general: "grey",
      };
      return colors[type] || "grey";
    },

    getTypeLabel(type) {
      const typeObj = this.questionTypes.find((t) => t.value === type);
      return typeObj ? typeObj.text : type;
    },

    clearSearch() {
      this.filter.search_keyword = null;
      this.page = 1;
      this.getData();
    },

    clearType() {
      this.filter.type = null;
      this.page = 1;
      this.getData();
    },

    clearStatus() {
      this.filter.is_active = null;
      this.page = 1;
      this.getData();
    },

    async getMasters() {
      try {
        const response = await axios.get("/masters", {
          params: {
            context: "questionTypes",
          },
        });

        if (response && response.data) {
          this.questionTypes = response.data.questionTypes || [];
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },
  },
};
</script>

<style scoped>
.striped-table tbody tr.table-row-even {
  background-color: #f5f5f5;
}
.striped-table tbody tr.table-row-odd {
  background-color: #ffffff;
}
</style>
